import { useNavigate } from 'react-router';
import { Tag, Dot } from '@/components/Tag';
import { dateTime, shortDate } from '@/lib/format';
import { DataTable, Two, type Col } from '../shipments/DataTable';
import { amountRows, targetOf, titleOf, TypeMark } from './ExceptionBits';
import { isOpen, recommended, responsibility, resolution, statusOf, stockImpact } from './status';
import { exceptionHref } from './api';
import type { ExceptionItem } from './types';
import s from './ExceptionsTable.module.css';

/**
 * 异常台账（桌面）：列照桌面稿 X1。首列「状态 / 类型」：状态是全行唯一实底标签，类型用色点。
 * 金额列只在有 orders.amount.read 且本页有非零金额时出现。
 */
export function ExceptionsTable({ items, showAmount, canClose, canResolve, onClose, onResolve }: {
  items: ExceptionItem[];
  showAmount: boolean;
  canClose: boolean;
  canResolve: boolean;
  onClose: (item: ExceptionItem) => void;
  onResolve: (item: ExceptionItem) => void;
}) {
  const nav = useNavigate();
  const cols: Col<ExceptionItem>[] = [
    {
      key: 'st', title: '状态 / 类型', w: 112, fix: true,
      render: (e) => <div className={s.stack}><Tag tone={statusOf(e.status).tone} sm>{statusOf(e.status).label}</Tag><TypeMark type={e.type} /></div>,
    },
    { key: 'order', title: '订单 / 客户', w: 170, render: (e) => <Two a={e.order.orderNo} b={e.order.customer.name} /> },
    {
      key: 'what', title: '异常内容', w: 300,
      render: (e) => { const t = targetOf(e); return <Two a={<b>{titleOf(e)}</b>} b={[t.bundle && `套装「${t.bundle}」的组件`, e.note].filter(Boolean).join(' · ')} />; },
    },
    { key: 'sh', title: '关联批次', w: 190, render: (e) => (e.shipment ? <Two a={e.shipment.shipmentNo} b={[e.shipment.logisticsCompany, e.shipment.trackingNo].filter(Boolean).join(' ')} /> : <Two a={<span className="em">订单级</span>} b="未关联批次" />) },
    { key: 'resp', title: '责任 / 处理', w: 150, render: (e) => <Two a={e.responsibility ? responsibility[e.responsibility] || e.responsibility : <span className="em">待判定</span>} b={e.resolutionAction ? resolution[e.resolutionAction] || e.resolutionAction : '—'} /> },
    { key: 'next', title: '推荐处理', w: 170, render: (e) => recommended(e) },
    { key: 'impact', title: '库存影响', w: 150, render: (e) => { const i = stockImpact(e); return <Dot tone={i.dot}>{i.label}</Dot>; } },
    { key: 'by', title: '登记', w: 110, render: (e) => <Two a={shortDate(e.createdAt)} b={e.createdBy?.name} /> },
    { key: 'upd', title: '更新时间', w: 120, render: (e) => <Two a={dateTime(e.updatedAt)} b={e.resolvedBy?.name} /> },
    { key: 'actions', title: '操作', w: 132, render: (e) => isOpen(e) ? <div className={s.actions} onKeyDown={(event) => event.stopPropagation()}>
      {canClose && <button type="button" onClick={(event) => { event.stopPropagation(); onClose(e); }}>关闭</button>}
      {canResolve && e.canOperate && <button type="button" className={s.primary} onClick={(event) => { event.stopPropagation(); onResolve(e); }}>处理</button>}
    </div> : null },
  ];
  if (showAmount && items.some((e) => amountRows(e).length)) {
    cols.splice(4, 0, {
      key: 'amt', title: '处理金额', w: 150, align: 'r',
      render: (e) => { const r = amountRows(e); return r.length ? <div className={s.amt}>{r.map(([k, v]) => <span key={k}><i>{k}</i>{v}</span>)}</div> : <span className="em">—</span>; },
    });
  }
  return <DataTable label="异常台账" cols={cols} rows={items} onRow={(e) => nav(exceptionHref(e), { state: { seed: e } })} />;
}
